import 'highcharts/modules/accessibility';

import * as Highcharts from 'highcharts';

import { ChangeEvent, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SdpFullStatus, SdpResponse, headPointPhysicalMeter } from '../../../models/SdpResponse';
import { UIStateProps, uiState } from '../../../atoms/uiAtom';
import {
  defaultSeriesVisibility,
  userPreferenceUnitState,
  userPreferencesState,
} from '../../../atoms/userPreferencesAtom/userPreferencesAtom';
import { useGetConsumptions, useGetConsumptionsBoundaries } from '../../../hooks/consumptions';
import { useGetEnergySharingInjections, useGetEnergySharingOffTakes } from '../../../hooks/meters';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { ReactComponent as BarChartIcon } from '../../../assets/chart-bar.svg';
import BottomSheet from '../../common/BottomSheet';
import ConsumptionDetails from './ConsumptionDetails';
import { ConsumptionResponse } from '../../../models/ConsumptionResponse';
import { ConsumptionUnitEnum } from '../../../enum/ConsumptionUnitEnum';
import DateRangePickerNavigator from '../../common/DateRangePickerNavigator';
import { DateTime } from 'luxon';
import EmptyState from '../../common/EmptyState';
import { EnergySharingVolumeResponse } from '../../../models/EnergySharingVolumeResponse';
import { ReactComponent as ExclamationIcon } from '../../../assets/exclamation.svg';
import { ReactComponent as EyeIcon } from '../../../assets/eye.svg';
import { ReactComponent as EyeSlashIcon } from '../../../assets/eye-slash.svg';
import { ReactComponent as FunnelIcon } from '../../../assets/funnel.svg';
import { ReactComponent as GraphSkeletonLoader } from '../../../assets/graph-skeleton-loader.svg';
import HighchartsReact from 'highcharts-react-official';
import { ReactComponent as InformationIcon } from '../../../assets/information.svg';
import Input from '../../forms/Input';
import ModalInformation from '../../modals/ModalInformation';
import { NumberUtils } from '../../../common/numberUtils';
import { PeriodEnum } from '../../../enum/PeriodEnum';
import PeriodGranularity from './PeriodGranularity';
import { SDPDirectionEnum } from '../../../enum/SDPDirectionEnum';
import { SDPStatusEnum } from '../../../enum/SDPStatusEnum';
import { SDPTariffChoiceEnum } from '../../../enum/SDPTariffChoiceEnum';
import { SeriesFilterEnum } from '../../../enum/SeriesFilterEnum';
import UnitSelector from './UnitSelector';
import { VolumeTypeEnum } from '../../../enum/VolumeTypeEnum';
import classNames from 'classnames';
import { renderToString } from 'react-dom/server';
import { roleAccessState } from '../../../atoms/roleAccessAtom';
import { routes } from '../../../routes';
import { use } from 'chai';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export interface ConsumptionChartProps {
  sdp?: SdpResponse | null;
  volumesType?: VolumeTypeEnum[];
  dataset: (
    consumptions?: ConsumptionResponse[],
    consumptionEnablePreviousCompare?: ConsumptionResponse[],
    energySharingInjections?: EnergySharingVolumeResponse[],
    energySharingOffTake?: EnergySharingVolumeResponse[],
    categories?: string[]
  ) => { name: string; data: (number | undefined)[]; color: string }[];
}

export default function ConsumptionChart({
  sdp,
  volumesType = [
    VolumeTypeEnum.INJECTION_DAY,
    VolumeTypeEnum.INJECTION_NIGHT,
    VolumeTypeEnum.CONSUMPTION_DAY,
    VolumeTypeEnum.CONSUMPTION_NIGHT,
  ],
  dataset,
}: ConsumptionChartProps) {
  const { t, i18n } = useTranslation();
  const roleAccess = useRecoilValue(roleAccessState);
  const hasInjection =
    volumesType.includes(VolumeTypeEnum.INJECTION_DAY) &&
    volumesType.includes(VolumeTypeEnum.INJECTION_NIGHT);
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const setUiState = useSetRecoilState<UIStateProps>(uiState);
  const [isOpenModalInformation, setIsOpenModalInformation] = useState(false);
  const userPreferenceUnit = useRecoilValue<ConsumptionUnitEnum>(userPreferenceUnitState);
  const [userPreferences, setUserPreferences] = useRecoilState(userPreferencesState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const [seriesVisibilitySource, setSeriesVisibilitySource] = useState<
    {
      type: SeriesFilterEnum;
      label: string;
      isTitle?: boolean;
      isEnable?: boolean;
      series: number[];
    }[]
  >([...defaultSeriesVisibility]);
  const { register, setValue } = useForm();
  const [consumptionEnablePreviousCompare, setConsumptionEnablePreviousCompare] =
    useState<boolean>(false);

  const handleSubmitPreviousCompare = (data: ChangeEvent<HTMLInputElement>) => {
    setConsumptionEnablePreviousCompare(data.target.checked);
  };

  useEffect(() => {
    setConsumptionEnablePreviousCompare(false);
    setValue('consumption-enable-previous-compare', false);
  }, [setValue, sdp]);

  useEffect(() => {
    if (
      consumptionEnablePreviousCompare === true &&
      userPreferenceUnit !== ConsumptionUnitEnum.KWH
    ) {
      setUserPreferences((prev) => ({
        ...prev,
        unit: ConsumptionUnitEnum.KWH,
      }));
    }
  }, [consumptionEnablePreviousCompare, setUserPreferences, userPreferenceUnit]);

  useEffect(() => {
    if (userPreferences.periodGranularity !== PeriodEnum.MONTHLY) {
      setConsumptionEnablePreviousCompare(false);
      setValue('consumption-enable-previous-compare', false);
    }
  }, [setValue, userPreferences.periodGranularity]);

  useEffect(() => {
    const enableFilterFromUserPreferences = (item: any) => {
      const foundItem = userPreferences.seriesVisibility.find(
        (itemSource: any) => itemSource.type === item.type
      );
      return {
        ...item,
        isEnable: foundItem ? foundItem.isEnable : true,
      };
    };
    switch (userPreferences.periodGranularity) {
      case PeriodEnum.QUARTERLY:
      case PeriodEnum.HOURLY:
        setUserPreferences((prev) => ({
          ...prev,
          seriesVisibility: seriesVisibilitySource
            .filter((itemSource) =>
              [
                SeriesFilterEnum.PREV_TOTAL_CONSUMPTION,
                SeriesFilterEnum.TOTAL_CONSUMPTION,
                userPreferenceUnit !== ConsumptionUnitEnum.KWH ||
                sdp?.direction !== SDPDirectionEnum.COMB ||
                !sdp?.hasBeenProsumer
                  ? null
                  : SeriesFilterEnum.TOTAL_INJECTION,
                userPreferenceUnit !== ConsumptionUnitEnum.KWH ||
                sdp?.direction !== SDPDirectionEnum.COMB ||
                !sdp?.hasBeenProsumer
                  ? null
                  : SeriesFilterEnum.PREV_TOTAL_INJECTION,
              ].includes(itemSource.type)
            )
            .map((item) => enableFilterFromUserPreferences(item)),
        }));
        break;
      case PeriodEnum.DAILY:
      case PeriodEnum.WEEKLY:
        setUserPreferences((prev) => ({
          ...prev,
          seriesVisibility: seriesVisibilitySource
            .filter((itemSource) =>
              [
                SeriesFilterEnum.PREV_TOTAL_CONSUMPTION,
                SeriesFilterEnum.TOTAL_CONSUMPTION,
                userPreferenceUnit !== ConsumptionUnitEnum.KWH ||
                sdp?.direction !== SDPDirectionEnum.COMB ||
                !sdp?.hasBeenProsumer
                  ? null
                  : SeriesFilterEnum.TOTAL_INJECTION,
                userPreferenceUnit !== ConsumptionUnitEnum.KWH ||
                sdp?.direction !== SDPDirectionEnum.COMB ||
                !sdp?.hasBeenProsumer
                  ? null
                  : SeriesFilterEnum.PREV_TOTAL_INJECTION,
                SeriesFilterEnum.DAY_NIGHT,
              ].includes(itemSource.type)
            )
            .map((item) => enableFilterFromUserPreferences(item)),
        }));
        break;
      case PeriodEnum.MONTHLY:
        setUserPreferences((prev) => ({
          ...prev,
          seriesVisibility: seriesVisibilitySource
            .filter((itemSource) =>
              [
                SeriesFilterEnum.PREV_TOTAL_CONSUMPTION,
                SeriesFilterEnum.TOTAL_CONSUMPTION,
                userPreferenceUnit !== ConsumptionUnitEnum.KWH ||
                sdp?.direction !== SDPDirectionEnum.COMB ||
                !sdp?.hasBeenProsumer
                  ? null
                  : SeriesFilterEnum.TOTAL_INJECTION,
                userPreferenceUnit !== ConsumptionUnitEnum.KWH ||
                sdp?.direction !== SDPDirectionEnum.COMB ||
                !sdp?.hasBeenProsumer
                  ? null
                  : SeriesFilterEnum.PREV_TOTAL_INJECTION,
                !consumptionEnablePreviousCompare ? SeriesFilterEnum.DAY_NIGHT : null,
                roleAccess?.myConsumption.hasWidgetEnergySharingCons &&
                !consumptionEnablePreviousCompare
                  ? SeriesFilterEnum.LOCAL_NETWORK_CONSUMPTION
                  : null,
                roleAccess?.myConsumption.hasWidgetEnergySharingProd &&
                !consumptionEnablePreviousCompare &&
                userPreferenceUnit === ConsumptionUnitEnum.KWH
                  ? SeriesFilterEnum.LOCAL_NETWORK_INJECTION
                  : null,
              ].includes(itemSource.type)
            )
            .map((item) => enableFilterFromUserPreferences(item)),
        }));
        break;
      case PeriodEnum.YEARLY:
        setUserPreferences((prev) => ({
          ...prev,
          seriesVisibility: seriesVisibilitySource
            .filter((itemSource) =>
              [
                SeriesFilterEnum.PREV_TOTAL_CONSUMPTION,
                SeriesFilterEnum.TOTAL_CONSUMPTION,
                userPreferenceUnit !== ConsumptionUnitEnum.KWH ||
                sdp?.direction !== SDPDirectionEnum.COMB ||
                !sdp?.hasBeenProsumer
                  ? null
                  : SeriesFilterEnum.TOTAL_INJECTION,
                userPreferenceUnit !== ConsumptionUnitEnum.KWH ||
                sdp?.direction !== SDPDirectionEnum.COMB ||
                !sdp?.hasBeenProsumer
                  ? null
                  : SeriesFilterEnum.PREV_TOTAL_INJECTION,
                SeriesFilterEnum.DAY_NIGHT,
                roleAccess?.myConsumption.hasWidgetEnergySharingCons
                  ? SeriesFilterEnum.LOCAL_NETWORK_CONSUMPTION
                  : null,
                roleAccess?.myConsumption.hasWidgetEnergySharingProd &&
                userPreferenceUnit === ConsumptionUnitEnum.KWH
                  ? SeriesFilterEnum.LOCAL_NETWORK_INJECTION
                  : null,
              ].includes(itemSource.type)
            )
            .map((item) => enableFilterFromUserPreferences(item)),
        }));
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sdp?.direction,
    sdp?.hasBeenProsumer,
    userPreferences.periodGranularity,
    userPreferenceUnit,
    roleAccess?.myConsumption.hasWidgetEnergySharingCons,
    roleAccess?.myConsumption.hasWidgetEnergySharingProd,
  ]);

  const getRangeDatePrevInterval = useCallback((period: PeriodEnum) => {
    switch (period) {
      case PeriodEnum.QUARTERLY:
      case PeriodEnum.HOURLY:
        return 1;
      case PeriodEnum.DAILY:
        return 15;
      case PeriodEnum.WEEKLY:
        return 11;
      case PeriodEnum.MONTHLY:
        return 6;
      case PeriodEnum.YEARLY:
        return 8;
      default:
        return 1;
    }
  }, []);

  const getRangeDateNextInterval = useCallback((period: PeriodEnum) => {
    switch (period) {
      case PeriodEnum.QUARTERLY:
      case PeriodEnum.HOURLY:
        return 1;
      case PeriodEnum.DAILY:
        return 15;
      case PeriodEnum.WEEKLY:
        return 14;

      case PeriodEnum.MONTHLY:
        return 5;
      case PeriodEnum.YEARLY:
        return 8;
      default:
        return 1;
    }
  }, []);

  const getRangeDateIntervalStartEndOfUnit = (period: PeriodEnum) => {
    switch (period) {
      case PeriodEnum.QUARTERLY:
      case PeriodEnum.HOURLY:
      case PeriodEnum.DAILY:
        return 'day';
      case PeriodEnum.WEEKLY:
        return 'week';
      case PeriodEnum.MONTHLY:
        return 'month';
      case PeriodEnum.YEARLY:
        return 'year';
      default:
        return 'month';
    }
  };

  const getRangeDateIntervalUnit = (period: PeriodEnum) => {
    switch (period) {
      case PeriodEnum.QUARTERLY:
      case PeriodEnum.HOURLY:
      case PeriodEnum.DAILY:
        return 'days';
      case PeriodEnum.WEEKLY:
        return 'weeks';
      case PeriodEnum.MONTHLY:
        return 'months';
      case PeriodEnum.YEARLY:
        return 'years';
      default:
        return 'days';
    }
  };

  const [rangeDate, setRangeDate] = useState<{
    startDate: string | null;
    endDate: string | null;
  }>({
    startDate: null,
    endDate: null,
  });
  const [hasManuallyChangeRange, setHasManuallyChangeRange] = useState(false);
  const [isOpenBottomSheet, setIsOpenBottomSheet] = useState(false);
  const [chartData, setChartData] = useState<any>({
    title: {
      text: '',
    },

    legend: {
      useHTML: true,
      enabled: false,
    },
    credits: {
      enabled: false,
    },

    tooltip: {
      useHTML: true,
      className: 'highcharts-legend',
      shared: true,
      backgroundColor: 'none',
      outside: true,
    },
    chart: {
      type: 'column',
      panKey: 'shift',
      scrollablePlotArea: {
        minWidth: 700,
        scrollPositionX: 0.5,
      },
      panning: {
        enabled: true,
        type: 'x',
      },
      load: () => {},
    },
    yAxis: {
      title: '',
    },
    scrollbar: {
      enabled: true,
    },
    xAxis: {
      crosshair: {
        color: '#ada9a7',
        zIndex: 3,
        width: 4,
      },
      categories: [],
      events: {
        setExtremes: function (context: any) {
          const series = context?.target.series;
          if (series && series[1]?.data.length) {
            series[1].data[series[1]?.data.length - 1].firePointEvent('click');
          }
        },
      },
    },
    plotOptions: {
      column: {
        shadow: false,
        stacking: 'normal',
      },
      series: {
        stacking: 'normal',
        point: {
          events: {
            click: function () {
              const self = this as any;
              const points = self.series.chart.series
                .filter((serie: any) => serie.visible)
                .map((serie: any) => serie.data[self.x])
                .filter((point: any) => point);
              if (points.length) {
                setSelectedChartPoint({
                  chartSelf: self,
                  category: self.category,
                  totalValue: self.series.chart.series[1].data[self.x]?.total,
                  points,
                });
              }
            },
          },
        },
      },
    },
    series: [],
  });

  const [selectedChartPoint, setSelectedChartPoint] = useState<{
    chartSelf: any;
    category: string;
    totalValue: number;
    points: any[];
  } | null>();

  const {
    isFetching: isFetchingConsumptionsBoundaries,
    data: consumptionsBoundaries,
    isError: isGetConsumptionsBoundariesError,
  } = useGetConsumptionsBoundaries({
    sdpId: sdp?.id,
    unit: userPreferenceUnit,
    period: userPreferences.periodGranularity,
  });

  const [previousCompareRangeDate, setPreviousCompareRangeDate] = useState<
    | {
        startDate: string;
        endDate: string;
      }
    | undefined
    | null
  >();

  const {
    isFetching: isFetchingConsumptions,
    data: consumptions,
    isError: isGetConsumptionsError,
  } = useGetConsumptions({
    sdpId: sdp?.id,
    unit: userPreferenceUnit,
    period: userPreferences.periodGranularity,
    volumesType: volumesType,
    startDate: rangeDate.startDate,
    endDate: rangeDate.endDate,
    meterLastUpdate: sdp ? headPointPhysicalMeter(sdp!.headpoint)!.lastAvailableDay : null,
    enabled: isFetchingConsumptionsBoundaries === false && !isGetConsumptionsBoundariesError,
  });

  const {
    isFetching: isPreviousCompareFetchingConsumptions,
    data: previousCompareConsumptions,
    isError: isGetPreviousCompareConsumptionsError,
  } = useGetConsumptions({
    sdpId: sdp?.id,
    unit: userPreferenceUnit,
    period: userPreferences.periodGranularity,
    volumesType: volumesType,
    startDate: previousCompareRangeDate?.startDate,
    endDate: previousCompareRangeDate?.endDate,
    meterLastUpdate: sdp ? headPointPhysicalMeter(sdp!.headpoint)!.lastAvailableDay : null,
    enabled:
      consumptionEnablePreviousCompare &&
      (previousCompareRangeDate as any) &&
      isFetchingConsumptionsBoundaries === false &&
      !isGetConsumptionsBoundariesError,
  });

  const {
    isFetching: isFetchingEnergySharingInjections,
    data: energySharingInjections,
    isError: isGetEnergySharingInjectionsError,
  } = useGetEnergySharingInjections({
    sdpId: sdp?.id,
    startDate: rangeDate.startDate,
    endDate: rangeDate.endDate,
    period: userPreferences.periodGranularity,
    enabled:
      isFetchingConsumptionsBoundaries === false &&
      !isGetConsumptionsBoundariesError &&
      (userPreferences.periodGranularity === PeriodEnum.MONTHLY ||
        userPreferences.periodGranularity === PeriodEnum.YEARLY) &&
      roleAccess?.myConsumption.hasWidgetEnergySharingProd,
  });

  const {
    isFetching: isFetchingEnergySharingOffTake,
    data: energySharingOffTake,
    isError: isGetEnergySharingOffTakeError,
  } = useGetEnergySharingOffTakes({
    sdpId: sdp?.id,
    startDate: rangeDate.startDate,
    endDate: rangeDate.endDate,
    period: userPreferences.periodGranularity,
    enabled:
      isFetchingConsumptionsBoundaries === false &&
      !isGetConsumptionsBoundariesError &&
      (userPreferences.periodGranularity === PeriodEnum.MONTHLY ||
        userPreferences.periodGranularity === PeriodEnum.YEARLY) &&
      roleAccess?.myConsumption.hasWidgetEnergySharingCons,
  });

  const uniqueFilter = (value: string, index: number, self: string[]) => {
    return self.indexOf(value) === index;
  };

  const getTooltipXAxisFormatByPeriod = useCallback(
    (period: PeriodEnum) => {
      const _hourFormat = new Map<string, string>([
        ['fr', "HH'h'mm"],
        ['en', 'hh:mm a'],
        ['nl', "HH'u'mm"],
      ]);
      switch (period) {
        case PeriodEnum.QUARTERLY:
          return `EEEE d MMMM - ${_hourFormat.get(i18n.language)}`;
        case PeriodEnum.HOURLY:
          return `EEEE d MMMM - ${_hourFormat.get(i18n.language)}`
            .replaceAll('mm', '')
            .replaceAll(':', '');
        case PeriodEnum.DAILY:
          return 'EEEE d MMMM';
        case PeriodEnum.WEEKLY:
          return `'${t('common.week.of')}' d MMMM`;
        case PeriodEnum.MONTHLY:
          return 'MMMM y';
        case PeriodEnum.YEARLY:
          return 'y';
        default:
          return `EEEE d MMMM -  ${_hourFormat.get(i18n.language)}`;
      }
    },
    [i18n.language, t]
  );

  const getXAxisFormatByPeriod = useCallback(
    (period: PeriodEnum) => {
      switch (period) {
        case PeriodEnum.QUARTERLY:
        case PeriodEnum.HOURLY:
          return 'HH:mm';
        case PeriodEnum.DAILY:
          return 'EEE d';
        case PeriodEnum.WEEKLY:
          return `'${t('common.week.of')}' dd/MM`;

        case PeriodEnum.MONTHLY:
          return 'MMMM';
        case PeriodEnum.YEARLY:
          return 'y';
        default:
          return 'dd/MM';
      }
    },
    [t]
  );

  const renderWarningMessage = useCallback(
    ({
      hasLabelNoReadingConsent,
      hasLabelNoEuroForInjection,
      hasLabelTHWarning,
      hasWarningCapable,
    }: {
      hasLabelNoReadingConsent?: boolean;
      hasLabelNoEuroForInjection?: boolean;
      hasLabelTHWarning?: boolean;
      hasWarningCapable?: boolean;
    }) => {
      let message: string = '';
      if (
        hasLabelTHWarning === true &&
        userPreferences.seriesVisibility.find((item) => item.type === SeriesFilterEnum.DAY_NIGHT)
          ?.isEnable
      ) {
        message += t('overview.meter.detail.consumption.simple.invoicing') + '\n';
      }
      if (hasLabelNoReadingConsent === true) {
        message += t('overview.meter.detail.consumption.no.reading.data') + '\n';
      }
      if (hasWarningCapable === true) {
        message += t('overview.meter.detail.consumption.not.capable') + '\n';
      }
      if (hasLabelNoEuroForInjection === true) {
        message += t('overview.meter.detail.consumption.no.euro.for.injection') + '\n';
      }
      return message;
    },
    [userPreferences.seriesVisibility, t]
  );

  const allConsumptionsResourcesLoaded = useMemo(() => {
    return (
      !isFetchingConsumptions &&
      !isGetConsumptionsError &&
      !isFetchingEnergySharingInjections &&
      !isGetEnergySharingInjectionsError &&
      !isFetchingEnergySharingOffTake &&
      !isGetEnergySharingOffTakeError &&
      !isFetchingConsumptionsBoundaries &&
      !isGetConsumptionsBoundariesError
    );
  }, [
    isFetchingConsumptions,
    isGetConsumptionsError,
    isFetchingEnergySharingInjections,
    isGetEnergySharingInjectionsError,
    isFetchingEnergySharingOffTake,
    isGetEnergySharingOffTakeError,
    isFetchingConsumptionsBoundaries,
    isGetConsumptionsBoundariesError,
  ]);

  const getConsumptionDateFilter = useCallback(() => {
    if (!userPreferences.consumptionDateFiler && consumptionsBoundaries?.data?.endDate) {
      return consumptionsBoundaries?.data?.endDate;
    }
    return userPreferences.consumptionDateFiler || new Date().toISOString();
  }, [consumptionsBoundaries?.data?.endDate, userPreferences.consumptionDateFiler]);

  useEffect(() => {
    if (
      isFetchingConsumptionsBoundaries === false &&
      isGetConsumptionsBoundariesError === false &&
      consumptionsBoundaries?.data?.startDate &&
      consumptionsBoundaries?.data?.endDate
    ) {
      const provisioningStartDate =
        userPreferences.periodGranularity === PeriodEnum.QUARTERLY
          ? DateTime.fromISO(getConsumptionDateFilter())
              .setZone('Europe/Brussels')
              .toJSDate()
              .toISOString()
          : DateTime.fromISO(getConsumptionDateFilter())
              .setZone('Europe/Brussels')
              .minus({
                [getRangeDateIntervalUnit(userPreferences.periodGranularity)]:
                  getRangeDatePrevInterval(userPreferences.periodGranularity),
              })
              .startOf(getRangeDateIntervalStartEndOfUnit(userPreferences.periodGranularity))
              .toJSDate()
              .toISOString();
      const startDate =
        provisioningStartDate < consumptionsBoundaries?.data?.startDate
          ? consumptionsBoundaries?.data?.startDate
          : provisioningStartDate;
      const provisioningEndDate =
        userPreferences.periodGranularity === PeriodEnum.QUARTERLY
          ? DateTime.fromISO(getConsumptionDateFilter())
              .setZone('Europe/Brussels')
              .toJSDate()
              .toISOString()
          : DateTime.fromISO(getConsumptionDateFilter())
              .setZone('Europe/Brussels')
              .plus({
                [getRangeDateIntervalUnit(userPreferences.periodGranularity)]:
                  getRangeDateNextInterval(userPreferences.periodGranularity),
              })
              .endOf(getRangeDateIntervalStartEndOfUnit(userPreferences.periodGranularity))
              .toJSDate()
              .toISOString();
      const endDate =
        provisioningEndDate > consumptionsBoundaries?.data?.endDate
          ? consumptionsBoundaries?.data?.endDate
          : provisioningEndDate;
      if (startDate <= endDate) {
        setRangeDate({
          startDate,
          endDate,
        });
      } else {
        setRangeDate({
          startDate,
          endDate: startDate,
        });
      }
    }
  }, [
    consumptionsBoundaries?.data?.endDate,
    consumptionsBoundaries?.data?.startDate,
    getConsumptionDateFilter,
    getRangeDateNextInterval,
    getRangeDatePrevInterval,
    isFetchingConsumptionsBoundaries,
    isGetConsumptionsBoundariesError,
    userPreferences.consumptionDateFiler,
    userPreferences.periodGranularity,
  ]);

  useEffect(() => {
    setTimeout(() => {
      if (
        consumptionEnablePreviousCompare &&
        rangeDate.startDate &&
        isPreviousCompareFetchingConsumptions === false &&
        isGetPreviousCompareConsumptionsError === false &&
        chartComponentRef.current?.chart.series.length
      ) {
        //Process the prev range date
        const prevInterval = getRangeDatePrevInterval(userPreferences.periodGranularity);
        const nextInterval = getRangeDateNextInterval(userPreferences.periodGranularity);
        const intervalUnit = getRangeDateIntervalUnit(userPreferences.periodGranularity);
        const startOfUnit = getRangeDateIntervalStartEndOfUnit(userPreferences.periodGranularity);
        const endOfUnit = getRangeDateIntervalStartEndOfUnit(userPreferences.periodGranularity);
        const minDate = consumptionsBoundaries?.data?.startDate;
        const provisioningStartDate = DateTime.fromISO(rangeDate.startDate!)
          .setZone('Europe/Brussels')
          .minus({
            [intervalUnit]: prevInterval + nextInterval,
          })
          .toJSDate()
          .toISOString();
        const previousStarDate = DateTime.fromISO(
          provisioningStartDate < (minDate ?? new Date().toISOString())
            ? (minDate ?? new Date().toISOString())
            : provisioningStartDate
        )
          .setZone('Europe/Brussels')
          .startOf(startOfUnit)
          .toJSDate()
          .toISOString();
        const previousEndDate = DateTime.fromISO(
          DateTime.fromISO(rangeDate.startDate!)
            .minus({
              [intervalUnit]: 1,
            })
            .toJSDate()
            .toISOString()
        )
          .setZone('Europe/Brussels')

          .endOf(endOfUnit)
          .toJSDate()
          .toISOString();
        setPreviousCompareRangeDate({
          startDate: previousStarDate,
          endDate: previousEndDate,
        });
        seriesVisibilitySource
          .filter(
            (item) =>
              item.type === SeriesFilterEnum.PREV_TOTAL_CONSUMPTION ||
              item.type === SeriesFilterEnum.PREV_TOTAL_INJECTION
          )
          .forEach((item) => {
            item.series.forEach((index) => {
              (chartComponentRef.current as any)?.chart.series[index]?.setVisible(true, false);
            });
          });
      } else if (
        chartComponentRef.current?.chart.series.length &&
        isPreviousCompareFetchingConsumptions === false &&
        isGetPreviousCompareConsumptionsError === false
      ) {
        // Hide the previous period data from the chart and update the chart accordingly
        seriesVisibilitySource
          .filter(
            (item) =>
              item.type === SeriesFilterEnum.PREV_TOTAL_CONSUMPTION ||
              item.type === SeriesFilterEnum.PREV_TOTAL_INJECTION
          )
          .forEach((item) => {
            item.series.forEach((index) => {
              (chartComponentRef.current as any)?.chart.series[index]?.setVisible(false, false);
            });
          });
      }
      (chartComponentRef.current as any)?.chart.redraw();
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chartComponentRef.current?.chart.series.length,
    consumptionEnablePreviousCompare,
    isPreviousCompareFetchingConsumptions,
    isGetPreviousCompareConsumptionsError,
    consumptionsBoundaries?.data?.startDate,
    rangeDate.startDate,
    userPreferences.periodGranularity,
  ]);

  useEffect(() => {
    if (allConsumptionsResourcesLoaded) {
      setSeriesVisibilitySource((prev) =>
        prev.map((item) => ({
          ...item,
          isEnable:
            userPreferences.seriesVisibility.find((itemSource) => itemSource.type === item.type)
              ?.isEnable ?? item.isEnable,
        }))
      );
    }
  }, [allConsumptionsResourcesLoaded, userPreferences.seriesVisibility]);

  useEffect(() => {
    if (userPreferences.periodGranularity === PeriodEnum.MONTHLY) {
      setUserPreferences((prev) => ({
        ...prev,
        seriesVisibility: prev.seriesVisibility.map((item) =>
          item.type === SeriesFilterEnum.LOCAL_NETWORK_CONSUMPTION ||
          item.type === SeriesFilterEnum.LOCAL_NETWORK_INJECTION ||
          item.type === SeriesFilterEnum.DAY_NIGHT
            ? {
                ...item,
                isEnable: !consumptionEnablePreviousCompare ? true : false,
              }
            : item
        ),
      }));
    }
  }, [
    setUserPreferences,
    userPreferences.periodGranularity,
    userPreferences.shouldShowByDefaultEnergyGuides,
    consumptionEnablePreviousCompare,
  ]);
  useEffect(() => {
    if (
      userPreferences.shouldShowByDefaultEnergyGuides &&
      (userPreferences.periodGranularity === PeriodEnum.MONTHLY ||
        userPreferences.periodGranularity === PeriodEnum.YEARLY)
    ) {
      setUserPreferences((prev) => ({
        ...prev,
        seriesVisibility: prev.seriesVisibility.map((item) =>
          item.type === SeriesFilterEnum.LOCAL_NETWORK_CONSUMPTION ||
          item.type === SeriesFilterEnum.LOCAL_NETWORK_INJECTION
            ? {
                ...item,
                isEnable: !consumptionEnablePreviousCompare ? true : false,
              }
            : item
        ),
      }));
    }
  }, [
    setUserPreferences,
    userPreferences.periodGranularity,
    userPreferences.shouldShowByDefaultEnergyGuides,
    consumptionEnablePreviousCompare,
  ]);

  useEffect(() => {
    if (allConsumptionsResourcesLoaded && chartComponentRef?.current?.chart.series) {
      setTimeout(() => {
        seriesVisibilitySource.forEach((visibility) => {
          const visibilityFoundItem = userPreferences.seriesVisibility.find(
            (item) => item.type === visibility.type
          );
          if (visibilityFoundItem) {
            visibilityFoundItem.series.forEach((index) => {
              (chartComponentRef.current as any)?.chart.series[index]?.setVisible(
                visibilityFoundItem.isEnable ?? false,
                false
              );
            });
          } else {
            visibility.series.forEach((index) => {
              (chartComponentRef.current as any)?.chart.series[index]?.setVisible(false, false);
            });
          }
        });
        (chartComponentRef.current as any)?.chart.redraw();
        setSelectedChartPoint((prev) => {
          const points = prev?.chartSelf?.series?.chart?.series
            .filter((serie: any) => serie.visible)
            .map((serie: any) => serie.data[prev?.chartSelf.x])
            .filter((point: any) => point);
          return prev ? { ...prev, points } : null;
        });
      }, 50);
    }
  }, [
    chartComponentRef.current?.chart.series,
    userPreferences.seriesVisibility,
    seriesVisibilitySource,
    allConsumptionsResourcesLoaded,
  ]);

  useEffect(() => {
    setUiState(() => ({
      hasError:
        isGetConsumptionsError ||
        isGetEnergySharingInjectionsError ||
        isGetEnergySharingOffTakeError ||
        isGetConsumptionsBoundariesError,
    }));
  }, [
    isGetConsumptionsError,
    isGetEnergySharingInjectionsError,
    isGetEnergySharingOffTakeError,
    isGetConsumptionsBoundariesError,
    setUiState,
  ]);

  useEffect(() => {
    const warningMessage = renderWarningMessage({
      hasLabelNoEuroForInjection: roleAccess?.myConsumption.hasLabelNoEuroForInjection,
      hasLabelNoReadingConsent: roleAccess?.myConsumption.hasLabelNoReadingConsent,
      hasWarningCapable: roleAccess?.settings.hasWarningCapable,
      hasLabelTHWarning: roleAccess?.myConsumption.hasLabelThOfftakeDetails,
    });
    if (warningMessage) {
      setUiState(() => ({
        hasWarning: {
          message: warningMessage,
          route: routes.OVERVIEW.path,
        },
      }));
    } else {
      setUiState(() => ({
        hasWarning: null,
      }));
    }
  }, [
    renderWarningMessage,
    roleAccess?.hiLo.hasLabelTHWarning,
    roleAccess?.myConsumption.hasLabelNoEuroForInjection,
    roleAccess?.myConsumption.hasLabelNoReadingConsent,
    roleAccess?.myConsumption.hasLabelThOfftakeDetails,
    roleAccess?.settings.hasWarningCapable,
    setUiState,
  ]);

  useEffect(() => {
    if (
      allConsumptionsResourcesLoaded &&
      chartComponentRef.current &&
      (consumptions?.data || energySharingInjections?.data || energySharingOffTake?.data)
    ) {
      const categoriesConsumptions = consumptions!.data.map((item) => item.time);
      const categories = [...categoriesConsumptions].filter(uniqueFilter);

      const categoriesLength = categories.length;

      if (categoriesLength > 0) {
        (chartComponentRef.current as any).chart.xAxis[0].startOnTick = false;
        (chartComponentRef.current as any).chart.xAxis[0].endOnTick = false;
        const min = categoriesLength - getExtremes(userPreferences.periodGranularity!);
        (chartComponentRef.current as any).chart.xAxis[0].setExtremes(
          min > 0 ? min : 0,
          categoriesLength - 1
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartComponentRef.current, consumptions, userPreferences.periodGranularity]);

  useEffect(() => {
    if (
      allConsumptionsResourcesLoaded &&
      (consumptions?.data || energySharingInjections?.data || energySharingOffTake?.data) &&
      sdp
    ) {
      const categoriesConsumptions = consumptions!.data.map((item) => item.time);
      const categories = [...categoriesConsumptions].filter(uniqueFilter);

      if (
        consumptions?.status === 200 ||
        energySharingInjections?.status === 200 ||
        energySharingOffTake?.status === 200
      ) {
        setChartData((prev: any) => ({
          ...prev,
          tooltip: {
            ...prev.tooltip,
            formatter: function () {
              const self = this as any;
              const items = self.points
                .map((point: any) => ({
                  label: point.series.name,
                  stackKey: point.series.stackKey,
                  value: NumberUtils.parse(Math.abs(point.y), 2),
                  badgeColor: point.series.color,
                  opacity: point.series.opacity,
                }))
                .filter((item: any) =>
                  userPreferences.seriesVisibility.find((visibility) => {
                    return (
                      visibility.isEnable === true &&
                      visibility.series.includes(
                        self.points.find((point: any) => point.series.name === item.label)?.series
                          .index
                      )
                    );
                  })
                );

              return renderToString(
                <ConsumptionDetails
                  className="highcharts-tooltip__content--large"
                  subtitle={`${
                    consumptionEnablePreviousCompare
                      ? DateTime.fromISO(self.category, {
                          locale: i18n.language,
                        })
                          .minus({ years: 1 })
                          .setZone('Europe/Brussels')
                          .toFormat(
                            getTooltipXAxisFormatByPeriod(userPreferences.periodGranularity)
                          )
                      : ''
                  }${
                    consumptionEnablePreviousCompare
                      ? ` - ${DateTime.fromISO(self.category, {
                          locale: i18n.language,
                        })

                          .setZone('Europe/Brussels')
                          .toFormat(
                            getTooltipXAxisFormatByPeriod(userPreferences.periodGranularity)
                          )}`
                      : `${DateTime.fromISO(self.category, {
                          locale: i18n.language,
                        })

                          .setZone('Europe/Brussels')
                          .toFormat(
                            getTooltipXAxisFormatByPeriod(userPreferences.periodGranularity)
                          )}`
                  }`}
                  unit={userPreferenceUnit}
                  items={items}
                />
              );
            },
          },
          yAxis: {
            ...prev.yAxis,
            labels: {
              formatter: (context: any) => {
                return Math.abs(context.value);
              },
            },
          },
          xAxis: {
            ...prev.xAxis,
            labels: {
              formatter: (context: any) => {
                return DateTime.fromISO(context.value, {
                  locale: i18n.language,
                })
                  .setZone('Europe/Brussels')
                  .toFormat(getXAxisFormatByPeriod(userPreferences.periodGranularity!));
              },
            },
            categories,
          },
          series: dataset(
            consumptions?.data,
            previousCompareConsumptions?.data,
            energySharingInjections?.data,
            energySharingOffTake?.data,
            categories
          ),
        }));
      }
    }
  }, [
    consumptions,
    dataset,
    userPreferences.seriesVisibility,
    energySharingInjections?.data,
    energySharingInjections?.status,
    energySharingOffTake?.data,
    energySharingOffTake?.status,
    getTooltipXAxisFormatByPeriod,
    getXAxisFormatByPeriod,
    hasInjection,
    i18n.language,
    sdp,
    t,
    userPreferences.periodGranularity,
    userPreferenceUnit,
    allConsumptionsResourcesLoaded,
    previousCompareConsumptions?.data,
    consumptionEnablePreviousCompare,
  ]);

  const handleClickPeriod = (period: PeriodEnum) => {
    setRangeDate({
      startDate: null,
      endDate: null,
    });
    if (period !== PeriodEnum.MONTHLY) {
      setConsumptionEnablePreviousCompare(false);
      setValue('consumption-enable-previous-compare', false);
    }
    setUserPreferences((prev) => ({
      ...prev,
      periodGranularity: period,
    }));
  };

  const handleChangeUnit = (value: ConsumptionUnitEnum) => {
    setUserPreferences((prev) => ({
      ...prev,
      unit: value,
    }));
  };

  const getExtremes = (period: PeriodEnum) => {
    switch (period) {
      case PeriodEnum.QUARTERLY:
        return 96;
      case PeriodEnum.HOURLY:
        return 72;
      case PeriodEnum.DAILY:
        return 31;
      case PeriodEnum.WEEKLY:
        return 26;
      case PeriodEnum.MONTHLY:
        return 12;
      case PeriodEnum.YEARLY:
        return 8;
      default:
        return 7;
    }
  };

  const handleClickInfo = () => {
    setIsOpenModalInformation(!isOpenModalInformation);
  };

  const handleClickFilterSeries = () => {
    setIsOpenBottomSheet(!isOpenBottomSheet);
  };

  const handleClickVisibilitySeries = (seriesIndex: number) => {
    setUserPreferences((prev) => ({
      ...prev,
      shouldShowByDefaultEnergyGuides: !(
        [
          SeriesFilterEnum.LOCAL_NETWORK_CONSUMPTION,
          SeriesFilterEnum.LOCAL_NETWORK_INJECTION,
        ].includes(userPreferences.seriesVisibility[seriesIndex].type) ||
        prev.shouldShowByDefaultEnergyGuides === false
      ),
      seriesVisibility: prev.seriesVisibility.map((item, index) =>
        index === seriesIndex ? { ...item, isEnable: !item.isEnable } : item
      ),
    }));
  };

  const getDateRangePickerDetailView = (period: PeriodEnum) => {
    switch (period) {
      case PeriodEnum.DAILY:
        return 'month';
      case PeriodEnum.MONTHLY:
      case PeriodEnum.WEEKLY:
        return 'year';
      case PeriodEnum.YEARLY:
        return 'decade';
      default:
        return 'month';
    }
  };

  const handleDateRangePickerNavigatorRangeChange = (startDate: string, endDate: string) => {
    setHasManuallyChangeRange(true);
    setRangeDate({
      startDate,
      endDate,
    });
  };

  const handleDateRangePickerNavigatorValueChange = (date: string) => {
    setHasManuallyChangeRange(false);
    setUserPreferences((prev) => ({
      ...prev,
      consumptionDateFiler: date,
    }));
  };

  const getInfoContent = () => (
    <Fragment>
      <h2>{t('modal.consumption.info.title')}</h2>
      <p>{t('modal.consumption.info.description')}</p>
    </Fragment>
  );

  return (
    <section className="widget consumption-chart">
      <Fragment>
        {isFetchingConsumptions ||
        isGetConsumptionsError ||
        isFetchingEnergySharingInjections ||
        isFetchingEnergySharingOffTake ||
        isFetchingConsumptionsBoundaries ||
        isGetEnergySharingInjectionsError ||
        isGetEnergySharingOffTakeError ||
        isGetConsumptionsBoundariesError ||
        !roleAccess ||
        !sdp ? (
          <GraphSkeletonLoader height={400} className="chart__skeleton" />
        ) : ((consumptions?.data &&
            ((consumptions?.data.length === 0 &&
              energySharingInjections?.data.length === 0 &&
              energySharingOffTake?.data.length === 0) ||
              SdpFullStatus(sdp!) === SDPStatusEnum.WAITING)) ||
            !consumptionsBoundaries?.data) &&
          !isFetchingConsumptions &&
          !isFetchingConsumptionsBoundaries &&
          !isFetchingEnergySharingInjections &&
          !isFetchingEnergySharingOffTake &&
          !isGetEnergySharingInjectionsError &&
          !isGetEnergySharingOffTakeError &&
          !isGetConsumptionsError &&
          !isGetConsumptionsBoundariesError ? (
          <Fragment>
            {SdpFullStatus(sdp!) !== SDPStatusEnum.WAITING && (
              <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-4">
                <PeriodGranularity
                  value={userPreferences.periodGranularity}
                  onClick={handleClickPeriod}
                />
                <DateRangePickerNavigator
                  hasManuallyChangeRange={hasManuallyChangeRange}
                  isDisable={userPreferences.periodGranularity === PeriodEnum.YEARLY}
                  periodGranularity={userPreferences.periodGranularity}
                  startDate={rangeDate.startDate}
                  endDate={rangeDate.endDate}
                  minDate={consumptionsBoundaries?.data?.startDate}
                  maxDate={consumptionsBoundaries?.data?.endDate}
                  hasNextDisabled={
                    DateTime.fromISO(rangeDate.endDate ?? new Date().toISOString())
                      .toLocal()
                      .toMillis() >=
                    DateTime.fromISO(
                      consumptionsBoundaries?.data?.endDate ?? new Date().toISOString()
                    )
                      .toLocal()
                      .toMillis()
                  }
                  hasPrevDisabled={
                    DateTime.fromISO(rangeDate.startDate ?? new Date().toISOString())
                      .toLocal()
                      .toMillis() <=
                    DateTime.fromISO(
                      consumptionsBoundaries?.data?.startDate ?? new Date().toISOString()
                    )
                      .toLocal()
                      .toMillis()
                  }
                  currentDate={getConsumptionDateFilter()}
                  onChangeRange={handleDateRangePickerNavigatorRangeChange}
                  onChange={handleDateRangePickerNavigatorValueChange}
                  intervalUnit={getRangeDateIntervalUnit(userPreferences.periodGranularity)}
                  startOfUnit={getRangeDateIntervalStartEndOfUnit(
                    userPreferences.periodGranularity
                  )}
                  endOfUnit={getRangeDateIntervalStartEndOfUnit(userPreferences.periodGranularity)}
                  prevInterval={getRangeDatePrevInterval(userPreferences.periodGranularity)}
                  nextInterval={getRangeDateNextInterval(userPreferences.periodGranularity)}
                  detailView={getDateRangePickerDetailView(userPreferences.periodGranularity)}
                />
                <Fragment>
                  <div className="d-flex flex-row align-items-center gap-3 d-none d-md-flex">
                    <Input
                      type="checkbox"
                      className="input--standalone"
                      register={register}
                      defaultValue={consumptionEnablePreviousCompare}
                      isDisabled={userPreferences.periodGranularity !== PeriodEnum.MONTHLY}
                      name="consumption-enable-previous-compare"
                      onChange={handleSubmitPreviousCompare}
                      suffix={
                        <span>
                          {t(
                            'overview.meter.detail.consumption.enable-previous-compare.input.label'
                          )}
                        </span>
                      }
                    />

                    {sdp?.tariffChoice === SDPTariffChoiceEnum.SOCIAL &&
                      !consumptionEnablePreviousCompare &&
                      roleAccess?.myConsumption.hasToggleEuro &&
                      roleAccess?.forecast.hasToggleEuro && (
                        <UnitSelector
                          className="unit-selector--large me-4"
                          value={userPreferenceUnit}
                          onChange={handleChangeUnit}
                        />
                      )}
                    <InformationIcon
                      role="button"
                      onClick={handleClickInfo}
                      className="icon icon--action icon--info me-2"
                      data-tip={renderToString(getInfoContent())}
                    />
                    <FunnelIcon
                      role="button"
                      data-test-id="filter-series-button"
                      className={classNames('icon  icon--ternary', {
                        'icon--action':
                          !consumptionEnablePreviousCompare &&
                          userPreferences.periodGranularity !== PeriodEnum.MONTHLY,
                        disabled:
                          consumptionEnablePreviousCompare &&
                          userPreferences.periodGranularity === PeriodEnum.MONTHLY,
                      })}
                      onClick={handleClickFilterSeries}
                    />
                  </div>

                  <div className="w-100 d-flex align-items-center  justify-content-end d-md-none">
                    <Input
                      type="checkbox"
                      className="input--standalone"
                      defaultValue={consumptionEnablePreviousCompare}
                      register={register}
                      name="consumption-enable-previous-compare"
                      onChange={handleSubmitPreviousCompare}
                      isDisabled={userPreferences.periodGranularity !== PeriodEnum.MONTHLY}
                      suffix={
                        <span>
                          {t(
                            'overview.meter.detail.consumption.enable-previous-compare.input.label'
                          )}
                        </span>
                      }
                    />

                    {sdp?.tariffChoice === SDPTariffChoiceEnum.SOCIAL &&
                      !consumptionEnablePreviousCompare &&
                      roleAccess?.myConsumption.hasToggleEuro &&
                      roleAccess?.forecast.hasToggleEuro && (
                        <UnitSelector
                          className="unit-selector--large me-4"
                          value={userPreferenceUnit}
                          onChange={handleChangeUnit}
                        />
                      )}
                    <FunnelIcon
                      data-test-id="filter-series-button"
                      onClick={handleClickFilterSeries}
                      role="button"
                      className={classNames('icon icon--ternary', {
                        'icon--action':
                          !consumptionEnablePreviousCompare &&
                          userPreferences.periodGranularity !== PeriodEnum.MONTHLY,
                        disabled:
                          consumptionEnablePreviousCompare &&
                          userPreferences.periodGranularity === PeriodEnum.MONTHLY,
                      })}
                    />
                  </div>
                </Fragment>
              </div>
            )}
            <EmptyState message={t('common.no.data')} icon={<BarChartIcon />} />
          </Fragment>
        ) : (
          <Fragment>
            <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-4">
              <PeriodGranularity
                value={userPreferences.periodGranularity}
                onClick={handleClickPeriod}
              />
              <DateRangePickerNavigator
                hasManuallyChangeRange={hasManuallyChangeRange}
                isDisable={userPreferences.periodGranularity === PeriodEnum.YEARLY}
                currentDate={getConsumptionDateFilter()}
                startDate={rangeDate.startDate}
                endDate={rangeDate.endDate}
                periodGranularity={userPreferences.periodGranularity}
                hasNextDisabled={
                  DateTime.fromISO(rangeDate.endDate ?? new Date().toISOString())
                    .toLocal()
                    .toMillis() >=
                  DateTime.fromISO(
                    consumptionsBoundaries?.data?.endDate ?? new Date().toISOString()
                  )
                    .toLocal()
                    .toMillis()
                }
                hasPrevDisabled={
                  DateTime.fromISO(rangeDate.startDate ?? new Date().toISOString())
                    .toLocal()
                    .toMillis() <=
                  DateTime.fromISO(
                    consumptionsBoundaries?.data?.startDate ?? new Date().toISOString()
                  )
                    .toLocal()
                    .toMillis()
                }
                minDate={consumptionsBoundaries?.data?.startDate}
                maxDate={consumptionsBoundaries?.data?.endDate}
                onChangeRange={handleDateRangePickerNavigatorRangeChange}
                onChange={handleDateRangePickerNavigatorValueChange}
                intervalUnit={getRangeDateIntervalUnit(userPreferences.periodGranularity)}
                startOfUnit={getRangeDateIntervalStartEndOfUnit(userPreferences.periodGranularity)}
                endOfUnit={getRangeDateIntervalStartEndOfUnit(userPreferences.periodGranularity)}
                prevInterval={getRangeDatePrevInterval(userPreferences.periodGranularity)}
                nextInterval={getRangeDateNextInterval(userPreferences.periodGranularity)}
                detailView={getDateRangePickerDetailView(userPreferences.periodGranularity)}
              />
              <Fragment>
                <div className="d-flex flex-row align-items-center gap-3 d-none d-md-flex">
                  <Input
                    type="checkbox"
                    className="input--standalone"
                    defaultValue={consumptionEnablePreviousCompare}
                    register={register}
                    isDisabled={userPreferences.periodGranularity !== PeriodEnum.MONTHLY}
                    name="consumption-enable-previous-compare"
                    onChange={handleSubmitPreviousCompare}
                    suffix={
                      <span>
                        {t('overview.meter.detail.consumption.enable-previous-compare.input.label')}
                      </span>
                    }
                  />

                  {sdp?.tariffChoice === SDPTariffChoiceEnum.SOCIAL &&
                    !consumptionEnablePreviousCompare &&
                    roleAccess?.myConsumption.hasToggleEuro &&
                    roleAccess?.forecast.hasToggleEuro && (
                      <UnitSelector
                        className="unit-selector--large me-4"
                        value={userPreferenceUnit}
                        onChange={handleChangeUnit}
                      />
                    )}
                  <InformationIcon
                    role="button"
                    onClick={handleClickInfo}
                    className="icon icon--action icon--info me-2"
                  />
                  <FunnelIcon
                    data-test-id="filter-series-button"
                    role="button"
                    className={classNames('icon  icon--ternary', {
                      'icon--action':
                        !consumptionEnablePreviousCompare &&
                        userPreferences.periodGranularity !== PeriodEnum.MONTHLY,
                      disabled:
                        consumptionEnablePreviousCompare &&
                        userPreferences.periodGranularity === PeriodEnum.MONTHLY,
                    })}
                    onClick={handleClickFilterSeries}
                  />
                </div>

                <div className="w-100 d-flex align-items-center  justify-content-end d-md-none">
                  <Input
                    type="checkbox"
                    className="input--standalone"
                    defaultValue={consumptionEnablePreviousCompare}
                    register={register}
                    onChange={handleSubmitPreviousCompare}
                    name="consumption-enable-previous-compare"
                    isDisabled={userPreferences.periodGranularity !== PeriodEnum.MONTHLY}
                    suffix={
                      <span>
                        {t('overview.meter.detail.consumption.enable-previous-compare.input.label')}
                      </span>
                    }
                  />

                  {sdp?.tariffChoice === SDPTariffChoiceEnum.SOCIAL &&
                    !consumptionEnablePreviousCompare &&
                    roleAccess?.myConsumption.hasToggleEuro &&
                    roleAccess?.forecast.hasToggleEuro && (
                      <UnitSelector
                        className="unit-selector--large me-4"
                        value={userPreferenceUnit}
                        onChange={handleChangeUnit}
                      />
                    )}
                  <FunnelIcon
                    data-test-id="filter-series-button"
                    onClick={handleClickFilterSeries}
                    role="button"
                    className={classNames('icon icon--ternary', {
                      'icon--action':
                        !consumptionEnablePreviousCompare &&
                        userPreferences.periodGranularity === PeriodEnum.MONTHLY,
                      disabled:
                        consumptionEnablePreviousCompare &&
                        userPreferences.periodGranularity === PeriodEnum.MONTHLY,
                    })}
                  />
                </div>
              </Fragment>
            </div>

            {selectedChartPoint && (
              <ConsumptionDetails
                hasInformationIcon
                className="highcharts-tooltip__content--small"
                subtitle={`${
                  consumptionEnablePreviousCompare
                    ? DateTime.fromISO(selectedChartPoint.category, {
                        locale: i18n.language,
                      })
                        .minus({ years: 1 })
                        .setZone('Europe/Brussels')
                        .toFormat(getTooltipXAxisFormatByPeriod(userPreferences.periodGranularity))
                    : ''
                }${
                  consumptionEnablePreviousCompare
                    ? ` - ${DateTime.fromISO(selectedChartPoint.category, {
                        locale: i18n.language,
                      })

                        .setZone('Europe/Brussels')
                        .toFormat(
                          getTooltipXAxisFormatByPeriod(userPreferences.periodGranularity)
                        )}`
                    : `${DateTime.fromISO(selectedChartPoint.category, {
                        locale: i18n.language,
                      })

                        .setZone('Europe/Brussels')
                        .toFormat(
                          getTooltipXAxisFormatByPeriod(userPreferences.periodGranularity)
                        )}`
                }`}
                onChangeUnit={
                  sdp.tariffChoice === SDPTariffChoiceEnum.SOCIAL &&
                  !consumptionEnablePreviousCompare &&
                  roleAccess?.myConsumption.hasToggleEuro &&
                  roleAccess?.forecast.hasToggleEuro
                    ? handleChangeUnit
                    : undefined
                }
                unit={userPreferenceUnit}
                items={selectedChartPoint!.points
                  ?.filter((point: any) => NumberUtils.isNumber(point.y))
                  .map((point: any) => ({
                    label: point.series?.name,
                    stackKey: point.series?.stackKey,
                    value: NumberUtils.parse(Math.abs(point.y), 2),
                    badgeColor: point.series?.color,
                    opacity: point.series?.opacity,
                  }))}
              />
            )}
            <HighchartsReact highcharts={Highcharts} options={chartData} ref={chartComponentRef} />
            <div className="consumption-chart__info">
              <ExclamationIcon className="icon  icon--info me-2" />
              <span>{t('overview.meter.detail.consumption.data.info')}</span>
            </div>
          </Fragment>
        )}
      </Fragment>
      <ModalInformation isOpen={isOpenModalInformation} onClickAction={handleClickInfo}>
        {getInfoContent()}
      </ModalInformation>

      <BottomSheet isOpen={isOpenBottomSheet} onClose={() => setIsOpenBottomSheet(false)}>
        <ul className="bottom-sheet__list">
          {userPreferences.seriesVisibility.map((item, index) => (
            <li
              key={index}
              style={{ display: item.isHidden ? 'none' : 'flex' }}
              className={classNames({
                'bottom-sheet__content__list__item': !item.isTitle,
                'pt-3': item.isTitle && index > 1,
                'bottom-sheet__content__list__item__separator':
                  item.isTitle && index > 1 && !item.isHidden,
                'bottom-sheet__header__title': item.isTitle,
                'bottom-sheet__content__list__item--disabled':
                  (userPreferences.periodGranularity !== PeriodEnum.MONTHLY &&
                    userPreferences.periodGranularity !== PeriodEnum.YEARLY &&
                    [
                      SeriesFilterEnum.LOCAL_NETWORK_CONSUMPTION,
                      SeriesFilterEnum.LOCAL_NETWORK_INJECTION,
                    ].includes(item.type)) ||
                  (item.type === SeriesFilterEnum.LOCAL_NETWORK_CONSUMPTION &&
                    !roleAccess?.myConsumption.hasWidgetEnergySharingCons) ||
                  (item.type === SeriesFilterEnum.LOCAL_NETWORK_INJECTION &&
                    !roleAccess?.myConsumption.hasWidgetEnergySharingProd),
              })}
            >
              <span
                data-test-id={`consumption-chart-series-visibility-${item.type.toLowerCase()}`}
                className={classNames('consumption-chart__badge me-2', {
                  'consumption-chart__badge__total-consumption':
                    item.type === SeriesFilterEnum.TOTAL_CONSUMPTION,
                  'consumption-chart__badge__day-night': item.type === SeriesFilterEnum.DAY_NIGHT,
                  'consumption-chart__badge__local-network-consumption':
                    item.type === SeriesFilterEnum.LOCAL_NETWORK_CONSUMPTION,
                  'consumption-chart__badge__total-injection':
                    item.type === SeriesFilterEnum.TOTAL_INJECTION,
                  'consumption-chart__badge__local-network-injection':
                    item.type === SeriesFilterEnum.LOCAL_NETWORK_INJECTION,
                })}
              ></span>
              <span className="w-100">{t(item.label)}</span>
              {item.isEnable ? (
                <EyeIcon
                  data-test-id={`consumption-chart-series-visibility-${item.type.toLowerCase()}-enable-button`}
                  role="button"
                  className="icon bottom-sheet__content__list__item__action"
                  onClick={() => handleClickVisibilitySeries(index)}
                />
              ) : (
                <EyeSlashIcon
                  data-test-id={`consumption-chart-series-visibility-${item.type.toLowerCase()}-disable-button`}
                  role="button"
                  className="icon bottom-sheet__content__list__item__action"
                  onClick={() => handleClickVisibilitySeries(index)}
                />
              )}
            </li>
          ))}
        </ul>
      </BottomSheet>
    </section>
  );
}
